import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Routes } from '~/domains/common/constants/routes';
import { logError } from '~/common/utils/log';
import {
  dismissOfferFeedItem,
  getOfferFeed,
  rejectOffer,
} from '~/services/marketplace/marketplace-service.service.api';
import type { OfferFeedItem } from '~/services/marketplace/marketplace-service.schemas';

const OFFER_FEED_KEY = ['marketplace', 'offer-feed'];
const STALE_TIME = 30_000;

export const useHomeOffers = () => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const [resolvedIds, setResolvedIds] = useState<string[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: OFFER_FEED_KEY,
    queryFn: ({ signal }) => getOfferFeed(signal),
    staleTime: STALE_TIME,
    onError: (error) => logError('useHomeOffers: failed to load the offer feed', error),
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });

  const unresolve = useCallback(
    (id: string) => setResolvedIds((ids) => ids.filter((existing) => existing !== id)),
    []
  );

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: OFFER_FEED_KEY }),
    [queryClient]
  );

  const { mutate: declineOffer, isLoading: isDeclining } = useMutation({
    mutationFn: rejectOffer,
    onError: (error, offerId) => {
      unresolve(offerId);
      logError('useHomeOffers: failed to decline an offer', error);
    },
    onSettled: invalidate,
  });

  const {
    mutate: dismissItem,
    mutateAsync: dismissItemAsync,
    isLoading: isDismissing,
  } = useMutation({
    mutationFn: dismissOfferFeedItem,
    onError: (error, offerId) => {
      unresolve(offerId);
      logError('useHomeOffers: failed to dismiss an offer', error);
    },
    onSettled: invalidate,
  });

  useEffect(() => {
    if (!data) {
      return;
    }

    const live = new Set(data.items.map((item) => item.offerId));
    setResolvedIds((ids) => ids.filter((id) => live.has(id)));
  }, [data]);

  const items = useMemo(
    () => (data?.items ?? []).filter((item) => !resolvedIds.includes(item.offerId)),
    [data, resolvedIds]
  );

  const resolve = useCallback((id: string) => setResolvedIds((ids) => [...ids, id]), []);

  const offersProps = useMemo(() => {
    if (isLoading || items.length === 0) {
      return undefined;
    }

    return {
      content: {
        type: 'offers' as const,
        offers: items,
        isBusy: isDeclining || isDismissing,
        onDecline: (item: OfferFeedItem) => {
          resolve(item.offerId);
          declineOffer(item.offerId);
        },
        onDismiss: (item: OfferFeedItem) => {
          resolve(item.offerId);
          dismissItem(item.offerId);
        },
        onView: (item: OfferFeedItem) => dismissItemAsync(item.offerId).catch(() => undefined),
      },
      totalCount: data?.total ?? items.length,
      onViewAllClick: () => router.push(Routes.marketplace()),
    };
  }, [
    declineOffer,
    dismissItem,
    dismissItemAsync,
    isDeclining,
    isDismissing,
    isLoading,
    data,
    items,
    resolve,
    router,
  ]);

  return { offersProps, isOffersLoading: isLoading };
};
